/**
 * Where one outcome came from: the QQI component it was imported from, and
 * whether its wording still matches.
 *
 * @module components/studio/panels/OutcomeProvenance
 */

import React from "react";

import { Badge, Button, FormField, MutedText, Panel, TextAreaField } from "@/components/ui";
import type { MIMLO, Module } from "@/types";
import { type CasLoChange, getModuleCasLoChanges } from "@/utils/cas-change-tracking";

/** Per outcome id: imported from CAS, and the drift from its source wording if any. */
export type OutcomeSources = ReadonlyMap<string, { change: CasLoChange | undefined }>;

/** The imported outcomes of a CAS module, or none for any other module. */
export function outcomeSources(module: Module | undefined): OutcomeSources {
  if (module?.casSource !== true) {
    return new Map();
  }
  const changes = new Map(getModuleCasLoChanges(module).map((change) => [change.mimloId, change]));
  return new Map(
    (module.casOriginalMimlos ?? []).map((original: MIMLO) => [
      original.id,
      { change: changes.get(original.id) },
    ]),
  );
}

export interface OutcomeProvenanceProps {
  index: number;
  source: { change: CasLoChange | undefined } | undefined;
  onRestore: (text: string) => void;
  onNote: (note: string) => void;
}

export function OutcomeProvenance({
  index,
  source,
  onRestore,
  onNote,
}: OutcomeProvenanceProps): React.ReactElement | null {
  if (!source) {
    return null;
  }
  const { change } = source;
  return (
    <div className="mt-2 space-y-2" data-testid={`module-outcome-source-${index + 1}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="primary" size="sm">
          QQI source
        </Badge>
        {change ? (
          <Badge tone="warning" size="sm" data-testid={`module-outcome-differs-${index + 1}`}>
            Differs from source
          </Badge>
        ) : null}
      </div>
      {change ? (
        <Panel density="compact" className="space-y-2">
          <MutedText>Source wording: {change.originalText}</MutedText>
          <FormField
            label="Why it differs"
            htmlFor={`module-outcome-note-${index + 1}`}
            helpText="Kept with the module for review against the CAS award."
          >
            <TextAreaField
              id={`module-outcome-note-${index + 1}`}
              value={change.note ?? ""}
              onChange={onNote}
              data-testid={`module-outcome-note-${index + 1}`}
            />
          </FormField>
          <Button
            size="xs"
            variant="ghost"
            icon="arrow-counter-clockwise"
            onClick={() => {
              // The source wording needs no reason, so the note goes with it.
              onRestore(change.originalText);
              onNote("");
            }}
          >
            Restore source wording
          </Button>
        </Panel>
      ) : null}
    </div>
  );
}
